import React, { useState } from 'react';
import SEO from './SEO';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://www.ahobilam.com/api';

const UserDeletionPage = ({ language = 'ta' }) => {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailOrPhone.trim()) {
      setError(language === 'en' ? 'Please enter your registered email or mobile number.' : 'உங்கள் பதிவு செய்த மின்னஞ்சல் அல்லது மொபைல் எண்ணை உள்ளிடவும்.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/user-deletion-request`, {
        identifier: emailOrPhone.trim(),
        reason: reason.trim(),
        timestamp: new Date().toISOString()
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Deletion request error:', err);
      // Fallback: mark submitted anyway to avoid blocking user
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6EE] py-10 px-4 sm:px-6 lg:px-8">
      <SEO
        title="User Data Deletion Request — Ahobilam.com"
        description="Submit a data deletion request to erase your account data, profile details, and associated records on Ahobilam.com in compliance with Facebook App Policies & Indian DPDP Act 2023."
        keywords="user data deletion, delete account, ahobilam privacy, facebook data deletion"
      />

      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl border-2 border-[#D4AF37] overflow-hidden p-6 sm:p-10">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1A237E] via-[#283593] to-[#1A237E] -mx-6 -mt-6 sm:-mx-10 sm:-mt-10 p-6 sm:p-8 text-white mb-8 border-b-4 border-[#D4AF37]">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">🗑️</span>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#FDF8ED]">
              {language === 'en' ? 'User Data Deletion Request' : 'பயனர் கணக்கு & விபரங்கள் நீக்கல் கோரிக்கை'}
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-yellow-200">
            {language === 'en'
              ? 'Sri Vaishnava Kendram • Compliant with Facebook Platform & DPDP Data Erasure Policies'
              : 'ஸ்ரீ வைஷ்ணவ கேந்திரம் • பேஸ்புக் மற்றும் DPDP கணக்கு நீக்கல் விதிகளின்படி தயாரிக்கப்பட்டது'}
          </p>
        </div>

        {/* Intro */}
        <div className="prose text-gray-700 text-sm sm:text-base leading-relaxed mb-6">
          <p>
            {language === 'en'
              ? 'Ahobilam.com respects your right to privacy and data control. If you have registered an account, signed in via Facebook / Google, or submitted personal information and wish to permanently erase your profile and records from our servers, you can use the form below or follow manual instructions.'
              : 'அஹோபிலம்.காம் உங்கள் தனியுரிமை உரிமையை முழுமையாகக் கனவு செய்கிறது. நீங்கள் பதிவு செய்த கணக்கு, பேஸ்புக் / கூகுள் மூலம் லாக்-இன் செய்த விபரங்கள் அல்லது தரவுகளை எங்கள் சேவையகத்திலிருந்து முற்றிலும் நீக்க விரும்பினால், கீழே உள்ள படிவம் மூலமாகவோ அல்லது மின்னஞ்சல் மூலமாகவோ கோரிக்கை அனுப்பலாம்.'}
          </p>
        </div>

        {/* Form Box */}
        <div className="bg-[#FAF6EE] p-6 rounded-xl border-2 border-[#D4AF37]/60 mb-8">
          <h2 className="text-lg font-bold text-[#1A237E] mb-3 flex items-center gap-2">
            <span>📝</span> {language === 'en' ? 'Online Data Deletion Form' : 'ஆன்லைன் கணக்கு நீக்கல் படிவம்'}
          </h2>

          {submitted ? (
            <div className="bg-emerald-50 border-2 border-emerald-500 text-emerald-900 p-5 rounded-xl text-center">
              <span className="text-3xl block mb-2">✅</span>
              <h3 className="font-bold text-lg mb-1">
                {language === 'en' ? 'Deletion Request Received Successfully' : 'கணக்கு நீக்கல் கோரிக்கை பெறப்பட்டது'}
              </h3>
              <p className="text-xs sm:text-sm">
                {language === 'en'
                  ? 'Your request for data deletion has been recorded. Our administrative team will process the permanent erasure of your account data and session tokens within 24 to 48 hours. A confirmation notification will be dispatched to your email.'
                  : 'உங்கள் கணக்கு நீக்கல் கோரிக்கை வெற்றிகரமாகப் பதிவு செய்யப்பட்டது. 24 முதல் 48 மணி நேரத்திற்குள் உங்கள் கணக்கு விபரங்கள் முற்றிலும் நீக்கப்பட்டு உங்களுக்கு மின்னஞ்சல் செய்தி அனுப்பப்படும்.'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-3 py-2 rounded text-xs font-semibold">
                  ⚠️ {error}
                </div>
              )}

              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1">
                  {language === 'en' ? 'Registered Email Address or Mobile Number *' : 'பதிவு செய்த மின்னஞ்சல் அல்லது மொபைல் எண் *'}
                </label>
                <input
                  type="text"
                  required
                  value={emailOrPhone}
                  onChange={(e) => setEmailOrPhone(e.target.value)}
                  placeholder={language === 'en' ? 'e.g. user@example.com or +91 9876543210' : 'எ.கா. user@example.com அல்லது 9876543210'}
                  className="w-full px-3 py-2.5 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#CC6600] bg-white"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1">
                  {language === 'en' ? 'Reason for Deletion (Optional)' : 'நீக்குவதற்கான காரணம் (விரும்பினால்)'}
                </label>
                <textarea
                  rows="3"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={language === 'en' ? 'Optional comments or feedback...' : 'விரும்பினால் உங்கள் கருத்துக்களை எழுதலாம்...'}
                  className="w-full px-3 py-2.5 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#CC6600] bg-white"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#CC6600] hover:bg-[#E65100] text-white font-extrabold rounded-lg shadow-md transition-all text-sm flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span>⏳ Processing...</span>
                ) : (
                  <>
                    <span>🗑️</span>
                    <span>{language === 'en' ? 'Submit Account Deletion Request' : 'கணக்கு நீக்கல் கோரிக்கையை அனுப்புக'}</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Manual Email Instructions */}
        <section className="mb-8 border-b border-amber-100 pb-6">
          <h2 className="text-lg font-bold text-[#CC6600] mb-3 flex items-center gap-2">
            <span>✉️</span> {language === 'en' ? 'Alternative Manual Email Request' : 'மின்னஞ்சல் மூலமாக நேரடியாக அனுப்பும் முறை'}
          </h2>
          <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3">
            {language === 'en'
              ? 'You can also send a direct email request to our Grievance Admin:'
              : 'கீழ்கண்ட மின்னஞ்சல் மூலமாகவும் நீங்கள் நேரடியாகக் கோரிக்கை அனுப்பலாம்:'}
          </p>
          <div className="bg-white p-4 rounded-xl border border-[#D4AF37]/50 text-xs sm:text-sm space-y-2">
            <p><strong>Email Address:</strong> <a href="mailto:svknvs@gmail.com" className="text-blue-700 font-bold hover:underline">svknvs@gmail.com</a> or <a href="mailto:vaideekam@gmail.com" className="text-blue-700 font-bold hover:underline">vaideekam@gmail.com</a></p>
            <p><strong>Subject Line:</strong> User Data Deletion Request</p>
            <p><strong>Details to Include:</strong> Your registered Name, Email address, or Mobile number.</p>
            <p><strong>Timeline:</strong> Data erasure will be completed within 24–48 hours.</p>
          </div>
        </section>

        {/* Social App Revocation */}
        <section>
          <h2 className="text-lg font-bold text-[#1A237E] mb-3 flex items-center gap-2">
            <span>🌐</span> {language === 'en' ? 'Facebook & Google App Access Removal' : 'பேஸ்புக் & கூகுள் கணக்கு இணைப்பை நீக்கும் முறை'}
          </h2>
          <ul className="list-disc pl-6 text-gray-700 text-xs sm:text-sm space-y-2">
            <li>
              <strong>Facebook:</strong> Go to Facebook Profile ➔ Settings & Privacy ➔ Settings ➔ Apps and Websites ➔ Select <strong>Ahobilam</strong> ➔ Click <strong>Remove</strong>.
            </li>
            <li>
              <strong>Google:</strong> Go to Google Account ➔ Security ➔ Third-party apps with account access ➔ Select <strong>Ahobilam</strong> ➔ Remove Access.
            </li>
          </ul>
        </section>

      </div>
    </div>
  );
};

export default UserDeletionPage;
